/**
===================================================
Module: frontend.src.components.TenantGroups.tsx
Description: Group management for tenant super administrators
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, apiService } from "../services/api";
import { TenantGroup } from "../types/tenant";
import TopBar from "./TopBar";
import ConfirmModal from "./ConfirmModal";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/TenantAdmin.css";

const TenantGroups: React.FC = () => {
  const { t } = useTranslation("tenant");

  const [groups, setGroups] = useState<TenantGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "" });
  const [editing, setEditing] = useState<TenantGroup | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TenantGroup | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const addNotification = (notification: Omit<Notification, "id">) => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { ...notification, id }]);
    return id;
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const reportError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.needsOtp) {
        addNotification({ type: "error", message: t("errors.otpRequired") });
        return;
      }
      addNotification({
        type: "error",
        message: error instanceof Error ? error.message : t("errors.generic"),
      });
    },
    [t],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getTenantGroups();
      setGroups(response.groups);
    } catch (error) {
      reportError(error);
    } finally {
      setIsLoading(false);
    }
  }, [reportError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!draft.name.trim()) {
      addNotification({ type: "error", message: t("common.required") });
      return;
    }
    setIsSaving(true);
    try {
      await apiService.createTenantGroup(draft.name.trim(), draft.description);
      addNotification({ type: "success", message: t("groups.createSuccess") });
      setIsCreating(false);
      setDraft({ name: "", description: "" });
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editing || !editing.name.trim()) {
      addNotification({ type: "error", message: t("common.required") });
      return;
    }
    setIsSaving(true);
    try {
      await apiService.updateTenantGroup(editing.id, {
        name: editing.name.trim(),
        description: editing.description ?? "",
      });
      addNotification({ type: "success", message: t("groups.updateSuccess") });
      setEditing(null);
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const requestDelete = (group: TenantGroup) => {
    if (group.is_default) {
      addNotification({
        type: "error",
        message: t("groups.deleteBlockedDefault"),
      });
      return;
    }
    if (group.project_count > 0) {
      addNotification({
        type: "error",
        message: t("groups.deleteBlockedProjects"),
      });
      return;
    }
    setPendingDelete(group);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsSaving(true);
    try {
      await apiService.deleteTenantGroup(pendingDelete.id);
      addNotification({ type: "success", message: t("groups.deleteSuccess") });
      setPendingDelete(null);
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const col = {
    name: t("groups.name"),
    description: t("groups.description"),
    members: t("groups.members"),
    projects: t("groups.projects"),
    actions: t("users.columns.actions"),
  };

  return (
    <div className="tenant-container">
      <div className="tenant-content">
        <TopBar />

        <div className="tenant-header-section">
          <div className="breadcrumb">
            <span
              className="breadcrumb-link"
              onClick={() => (window.location.href = "/projects")}
            >
              {t("common:home", { defaultValue: "Home" })}
            </span>
            <span className="breadcrumb-separator">/</span>
            <span>{t("groups.title")}</span>
          </div>

          <div className="tenant-title-row">
            <div>
              <h1>{t("groups.title")}</h1>
              <p className="tenant-subtitle">{t("groups.subtitle")}</p>
            </div>
            <button
              className="tbtn tbtn-primary"
              onClick={() => setIsCreating(true)}
            >
              {t("groups.create")}
            </button>
          </div>
        </div>

        <div className="tenant-table-wrap">
          <table className="tenant-table">
            <thead>
              <tr>
                <th>{col.name}</th>
                <th>{col.description}</th>
                <th>{col.members}</th>
                <th>{col.projects}</th>
                <th>{col.actions}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr className="row-message">
                  <td colSpan={5}>{t("groups.loading")}</td>
                </tr>
              )}
              {!isLoading && groups.length === 0 && (
                <tr className="row-message">
                  <td colSpan={5}>{t("groups.empty")}</td>
                </tr>
              )}
              {!isLoading &&
                groups.map((group) => (
                  <tr key={group.id}>
                    <td data-label={col.name}>
                      <span className="cell-primary">{group.name}</span>
                      {group.is_default && (
                        <span className="group-chip default-chip">
                          {t("groups.isDefault")}
                        </span>
                      )}
                    </td>
                    <td data-label={col.description}>
                      {group.description || <span className="muted">—</span>}
                    </td>
                    <td data-label={col.members}>{group.member_count}</td>
                    <td data-label={col.projects}>{group.project_count}</td>
                    <td className="actions-cell" data-label={col.actions}>
                      <div className="tbtn-group">
                        <button
                          className="tbtn tbtn-outline tbtn-sm"
                          onClick={() => setEditing({ ...group })}
                        >
                          {t("groups.edit")}
                        </button>
                        {!group.is_default && (
                          <button
                            className="tbtn tbtn-danger tbtn-sm"
                            onClick={() => requestDelete(group)}
                          >
                            {t("groups.delete")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {(isCreating || editing) && (
        <div className="modal-overlay">
          <div className="tenant-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>
                {isCreating ? t("groups.createTitle") : t("groups.editTitle")}
              </h2>
            </div>

            <div className="form-section">
              <label>{t("groups.name")}</label>
              <input
                type="text"
                value={isCreating ? draft.name : editing?.name ?? ""}
                onChange={(e) =>
                  isCreating
                    ? setDraft({ ...draft, name: e.target.value })
                    : setEditing({ ...editing!, name: e.target.value })
                }
                required
              />

              <label>{t("groups.description")}</label>
              <input
                type="text"
                value={
                  isCreating ? draft.description : editing?.description ?? ""
                }
                onChange={(e) =>
                  isCreating
                    ? setDraft({ ...draft, description: e.target.value })
                    : setEditing({ ...editing!, description: e.target.value })
                }
              />
            </div>

            <div className="modal-actions">
              <button
                className="tbtn tbtn-outline"
                onClick={() => {
                  setIsCreating(false);
                  setEditing(null);
                }}
                disabled={isSaving}
              >
                {t("common.cancel")}
              </button>
              <button
                className="tbtn tbtn-primary"
                onClick={isCreating ? handleCreate : handleUpdate}
                disabled={isSaving}
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <ConfirmModal
          title={t("groups.deleteTitle")}
          message={t("groups.deleteConfirm", { name: pendingDelete.name })}
          confirmLabel={t("confirm.delete")}
          cancelLabel={t("confirm.keep")}
          danger
          busy={isSaving}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default TenantGroups;
